import { Check, ChevronDown } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { workspacePageSections } from '../../config/pages';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const adminSections = workspacePageSections.filter(
  (section) => section.label === 'Admin' || section.label === 'Access'
);

function WorkspaceAdminNav() {
  const { activePage, selectNav } = useWorkspace();
  const menuRef = useRef<HTMLDetailsElement>(null);
  const currentPage = adminSections
    .flatMap((section) => section.items)
    .find((item) => item.id === activePage);

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !menuRef.current?.contains(event.target)
      ) {
        if (menuRef.current) menuRef.current.open = false;
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, []);

  return (
    <nav className="scopePageNav" aria-label="Workspace navigation">
      <span className="scopePageNavContext">Workspace settings</span>
      <details
        className="scopePageMenu"
        ref={menuRef}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && menuRef.current) {
            menuRef.current.open = false;
            menuRef.current.querySelector('summary')?.focus();
          }
        }}
      >
        <summary
          className="scopePageMenuTrigger"
          aria-label="Choose a workspace settings page"
        >
          <strong>{currentPage?.label ?? 'Settings'}</strong>
          <ChevronDown size={15} aria-hidden="true" />
        </summary>
        <div className="scopePageMenuPanel scopePageMenuPanel--workspace">
          {adminSections.map((section) => (
            <section
              key={section.label}
              className="scopePageMenuGroup"
              aria-label={section.label}
            >
              <h2>{section.label}</h2>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = activePage === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`scopePageMenuItem${active ? ' active' : ''}`}
                    onClick={() => {
                      selectNav(item.id);
                      if (menuRef.current) menuRef.current.open = false;
                    }}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon size={15} aria-hidden="true" />
                    <span>{item.label}</span>
                    {active ? <Check size={14} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </section>
          ))}
        </div>
      </details>
    </nav>
  );
}

export { WorkspaceAdminNav };
